// Updated HomePage.tsx - RTL-aware layout for LightningTV absolute positioning
//@ts-ignore
import { createEffect, createSignal, onCleanup, onMount, Show, createMemo, createResource } from "solid-js";
import { createStore } from "solid-js/store";
import { View, Text, hexColor } from "@lightningtv/solid";
import { useNavigate, useParams } from "@solidjs/router";
import {
  isVpnMode,
  playListUrl,
  playListUsername,
  setIsPlaying,
  setIsVpnMode,
} from "../../state";
import { isRTL, t, intlLocale, fontFamily } from "../translations/configuration";
import { rtlX } from "../utils/rtl";
import { createInfiniteScroll } from "../components/pagination";
import { shouldBlockAccess } from "../api/providers/killSwitch";
import Header from "../components/Header";
import allChannels from "../api/providers/allChannels";
import allMovies from "../api/providers/allMovies";
import allSeries from "../api/providers/allSeries";
import { checkProxyStatus, isProxyHealthOk, showProxyHealthMessage } from "../pages/proxyHealth/proxyHealthCall";

// ─── LOADING STATE ────────────────────────────────────────────────────────────
// Kept at module level so LoadingScreen (a sibling component) can read it.
// Reset to loading=true on every HomePage mount to avoid showing stale counts.
const [loadingStates, setLoadingStates] = createStore({
  vod: { count: null as string | null, loading: true },
  live: { count: null as string | null, loading: true },
  series: { count: null as string | null, loading: true },
});
const [loadingDots, setLoadingDots] = createSignal(0);
const loadingText = () => `${t("loading_base")}${".".repeat(loadingDots())}`;

// ─── LAYOUT CONSTANTS (RTL-aware) ────────────────────────────────────────────
const HERO_W = 900;
const HERO_H = 860;
const HERO_LTR_X = 64;

const SIDE_W = 850;
const SIDE_H = 428;
const SIDE_LTR_X = 1000;
const SIDE_TOP_Y = 140;
const SIDE_BOT_Y = SIDE_TOP_Y + SIDE_H + 24;

// ─── COLORS ───────────────────────────────────────────────────────────────────
const C = {
  bg: "#0d1117",
  cardDark: "#3666a8",
  cardDarker: "#13181f",
  iconBg: "#1f2937",
  blue: "#2563eb",
  blueDim: "#1d4ed8",
  red: "#ef4444",
  green: "#22c55e",
  textPrimary: "#f0f6fc",
  textMuted: "#8b949e",
};

// ─── HERO CARD — Live TV ──────────────────────────────────────────────────────
function HeroCard(props: any) {
  const [focused, setFocused] = createSignal(false);

  const countText = createMemo(() => {
    const s = loadingStates["live"];
    if (!s) return `0 ${t("channels")}`;
    if (s.loading) return `${loadingText()} ${t("channels")}`;
    return s.count ? `${s.count} ${t("channels")}` : `0 ${t("channels")}`;
  });

  return (
    <View
      ref={(el) => props.onRef?.(el)}
      autofocus
      x={rtlX(HERO_LTR_X, HERO_W, isRTL())}
      y={144}
      width={HERO_W}
      height={HERO_H}
      borderRadius={26}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onEnter={props.onEnter}
      onRight={() => isRTL() ? null : props.onRight?.()}
      onLeft={() => isRTL() ? props.onRight?.() : null}
      onUp={props.onUp}
    >
      {/* ── Focus border lives here — never clipped ── */}
      <Show when={focused()}>
        <View
          x={-4}
          y={-4}
          width={900}
          height={868}
          borderRadius={26}
          color={hexColor("#2563eb")}
          zIndex={-1}
        />
      </Show>

      {/* ── Inner card with clipping for rounded image ── */}
      <View
        x={0}
        y={0}
        width={900}
        height={860}
        borderRadius={22}
        clipping
      >
        {/* Full card background image */}
        <View
          src="https://i.ibb.co/W4BnJjDg/live-Bck.png"
          x={0}
          y={0}
          width={890}
          height={860}
          borderRadius={22}
        />
        <View
          x={0}
          y={0}
          width={890}
          height={860}
          color={hexColor("#000000")}
          alpha={0.72}
          borderRadius={22}
        />

        {/* ── LIVE NOW pill ── */}
        <View x={24} y={24} width={190} height={52} borderRadius={26} color={hexColor(C.red)}>
          <View x={18} y={18} width={16} height={16} borderRadius={8} color={hexColor("#ffffff")} />
          <Text x={44} y={14} style={{ fontSize: 22, color: hexColor("#ffffff"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
            {t("live_now")}
          </Text>
        </View>

        {/* ── TV icon badge ── */}
        <View x={24} y={510} width={70} height={70} borderRadius={14} color={hexColor(C.iconBg)}>
          <View x={13} y={13} src="https://i.ibb.co/Mk0Y8F85/Tv.png" width={44} height={44} />
        </View>

        {/* ── Title ── */}
        <Text x={24} y={596} style={{ fontSize: 52, color: hexColor(C.textPrimary), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
          {t("bigLiveTv")}
        </Text>

        {/* ── Channel count ── */}
        <Text x={24} y={660} style={{ fontSize: 26, color: hexColor(C.textMuted), fontFamily: fontFamily() }}>
          {countText()}
        </Text>

        {/* ── Blue accent bar ── */}
        <View x={24} y={714} width={70} height={5} borderRadius={3} color={hexColor(C.blue)} />

        {/* ── Tag pills ── */}
        <View x={24} y={740} width={108} height={52} borderRadius={26} color={hexColor(C.red)}>
          <Text x={20} y={12} style={{ fontSize: 22, color: hexColor("#ffffff"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>{t("news")}</Text>
        </View>
        <View x={148} y={740} width={130} height={52} borderRadius={26} color={hexColor(C.blue)}>
          <Text x={16} y={12} style={{ fontSize: 22, color: hexColor("#ffffff"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>{t("sport")}</Text>
        </View>
        <View x={294} y={740} width={108} height={52} borderRadius={26} color={hexColor(C.green)}>
          <Text x={20} y={12} style={{ fontSize: 22, color: hexColor("#ffffff"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>{t("kids")}</Text>
        </View>
      </View>
    </View>
  );
}

// ─── SIDE CARD ────────────────────────────────────────────────────────────────
function SideCard(props: any) {
  const [focused, setFocused] = createSignal(false);
  const suffix = () => props.symbol === "vod" ? t("movies") : t("series");

  const countText = createMemo(() => {
    const s = loadingStates[props.symbol];
    if (!s) return `0 ${suffix()}`;
    if (s.loading) return `${loadingText()} ${suffix()}`;
    return s.count ? `${s.count} ${suffix()}` : `0 ${suffix()}`;
  });

  const H = props.height;
  const W = props.width;

  return (
    <View
      ref={(el) => props.onRef?.(el)}
      x={props.x}
      y={props.y}
      width={W}
      height={H}
      borderRadius={24}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onEnter={props.onEnter}
      onLeft={props.onLeft}
      onRight={props.onRight}
      onUp={props.onUp}
      onDown={props.onDown}
    >
      {/* ── Focus border ── */}
      <Show when={focused()}>
        <View
          x={-4}
          y={-4}
          width={W + 8}
          height={H + 8}
          borderRadius={26}
          color={hexColor("#2563eb")}
          zIndex={-1}
        />
      </Show>

      {/* ── Inner card with clipping ── */}
      <View
        x={0}
        y={0}
        width={W}
        height={H}
        borderRadius={20}
        clipping
      >
        {/* ── Background image ── */}
        <View
          src={props.imageSrc}
          x={0}
          y={0}
          width={W}
          height={H}
          borderRadius={20}
        />

        {/* ── Dark overlay ── */}
        <View
          x={0}
          y={0}
          width={W}
          height={H}
          color={hexColor("#000000")}
          alpha={0.72}
          borderRadius={20}
        />

        {/* ── Icon badge ── */}
        <View x={28} y={24} width={66} height={66} borderRadius={13} color={hexColor(C.iconBg)}>
          <View x={11} y={11} src={props.iconSrc} width={44} height={44} />
        </View>

        {/* ── Title ── */}
        <Text x={28} y={H - 130} style={{ fontSize: 48, color: hexColor(C.textPrimary), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
          {props.title}
        </Text>

        {/* ── Count ── */}
        <Text x={28} y={H - 72} style={{ fontSize: 26, color: hexColor(C.textMuted), fontFamily: fontFamily() }}>
          {countText()}
        </Text>
      </View>
    </View>
  );
}

// ─── ICON BUTTON ─────────────────────────────────────────────────────────────
function IconButton(props: any) {
  const [focused, setFocused] = createSignal(false);

  return (
    <View
      ref={(el) => props.onRef?.(el)}
      x={props.x}
      y={props.y}
      width={80}
      height={80}
      borderRadius={40}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onRight={props.onRight}
      onLeft={props.onLeft}
      onDown={props.onDown}
      onUp={props.onUp}
      onEnter={props.onEnter}
    >
      {/* Focus border */}
      <Show when={focused()}>
        <View
          x={-2} y={-2}
          width={84} height={84}
          borderRadius={44}
          color={hexColor(C.blue)}
          zIndex={-1}
        />
      </Show>

      {/* Inner circle */}
      <View
        x={0} y={0}
        width={80} height={80}
        borderRadius={40}
        color={hexColor(C.iconBg)}
      >
        <View x={19} y={19} src={props.src} width={42} height={42} />
      </View>
    </View>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const HomePage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [time, setTime] = createSignal("");
  const [date, setDate] = createSignal("");
  const [showKillSwitchBlockModal, setShowKillSwitchBlockModal] = createSignal(false);

  let col2, settingRef, killSwitchBlockModalOkRef, headerRef;
  let heroRef, moviesRef, seriesRef;

  // allChannels() resolves to just the number of live channels, not a page of items.
  const [liveCount, { refetch: refetchLive }] = createResource(allChannels());
  const allMoviesList = createInfiniteScroll(allMovies());
  const allSeriesList = createInfiniteScroll(allSeries());

  // Loading text animation
  createEffect(() => {
    const interval = setInterval(() => {
      setLoadingDots(d => (d + 1) % 4);
    }, 500);
    onCleanup(() => clearInterval(interval));
  });

  const updateTime = () => {
    const now = new Date();
    setTime(`${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`);
    setDate(`${now.toLocaleDateString(intlLocale(), { weekday: "long" })}, ${now.toLocaleDateString(intlLocale(), { month: "short", day: "numeric" })}`);
  };

  onMount(() => {
    updateTime();
    const interval = setInterval(updateTime, 30000);
    onCleanup(() => clearInterval(interval));
  });

  createEffect(() => { setIsPlaying(false); });

  const checkKillSwitchAndNavigate = async (fn) => {
    const isHealthy = await isProxyHealthOk(t);
    if (!isHealthy && isVpnMode()) { showProxyHealthMessage(); return; }
    if (shouldBlockAccess()) {
      setShowKillSwitchBlockModal(true);
      setTimeout(() => { killSwitchBlockModalOkRef?.setFocus(); }, 100);
      return;
    }
    fn();
  };

  // Block navigation into a section only while its catalog is still loading.
  // A settled "0" count is NOT a reason to block: it can just as easily mean the
  // bulk download timed out or errored (slow TV network, huge playlist) as that
  // the source is genuinely empty, and blocking would strand the user with no way
  // to recover for the rest of the session. The destination screens load their
  // own categories, so letting the user in doubles as a retry.
  const isSourceReady = (key: "live" | "vod" | "series") => !loadingStates[key].loading;

  // If the count settled at "0", re-attempt the catalog fetch in the background
  // (failed results aren't cached, so this actually hits the network again).
  const retryIfEmpty = (key: "live" | "vod" | "series") => {
    if (loadingStates[key].count !== "0") return;
    setLoadingStates(key, { count: null, loading: true });
    if (key === "live") {
      refetchLive();
      return;
    }
    const list = key === "vod" ? allMoviesList : allSeriesList;
    list.setPages([]);
    list.setEnd(false);
    list.refetch();
  };

  const liveTV = () => {
    if (!isSourceReady("live")) return;
    retryIfEmpty("live");
    checkKillSwitchAndNavigate(() => { navigate("/channel-menu"); setIsPlaying(true); });
  };
  const movies = () => {
    if (!isSourceReady("vod")) return;
    retryIfEmpty("vod");
    checkKillSwitchAndNavigate(() => { navigate("/movies"); setIsPlaying(true); });
  };
  const series = () => {
    if (!isSourceReady("series")) return;
    retryIfEmpty("series");
    checkKillSwitchAndNavigate(() => { navigate("/series"); setIsPlaying(true); });
  };

  const togglePrivacyMode = async () => {
    const proxyHealthy = await checkProxyStatus();
    if (!proxyHealthy && !isVpnMode()) { showProxyHealthMessage(); return; }
    setIsVpnMode(prev => {
      const newMode = !prev;
      localStorage.setItem("vpn", newMode ? "on" : "off");
      return newMode;
    });
  };

  // Re-run once credentials/URL are confirmed present. The catalog fetches
  // kick off at mount regardless of whether playListUsername/playListUrl have
  // finished loading (e.g. auto-login restoring them from storage) — if they
  // fire too early the request fails and settles to an empty result. Clearing
  // the accumulated pages and forcing a refetch here lets the counts recover
  // once the real credentials land, instead of being stuck at "0" for the
  // rest of the session.
  createEffect(() => {
    const username = playListUsername();
    const baseUrl = playListUrl();
    if (username && baseUrl) {
      setLoadingStates("live", { count: null, loading: true });
      setLoadingStates("vod", { count: null, loading: true });
      setLoadingStates("series", { count: null, loading: true });

      refetchLive();

      allMoviesList.setPages([]);
      allMoviesList.setEnd(false);
      allMoviesList.refetch();

      allSeriesList.setPages([]);
      allSeriesList.setEnd(false);
      allSeriesList.refetch();
    }
  });

  // Settle on `.loading()` (the underlying fetch has resolved or failed), not on
  // `pages().length > 0` — the old check meant a source that genuinely has 0 items,
  // or that timed out/errored and fell back to an empty array, never flipped
  // `loading` to false and left its card (and, since the whole page used to be
  // gated on all three, the entire Home screen) spinning forever.
  createEffect(() => {
    if (liveCount.loading) return;
    const count = liveCount() ?? 0;
    console.log("LiveCount resolved:", count);
    setLoadingStates("live", { count: count > 0 ? `+${count.toLocaleString()}` : "0", loading: false });
  });

  createEffect(() => {
    if (allMoviesList.loading()) return;
    const count = allMoviesList.pages()?.length ?? 0;
    console.log("MoviesList resolved:", count);
    setLoadingStates("vod", { count: count > 0 ? `+${count.toLocaleString()}` : "0", loading: false });
  });

  createEffect(() => {
    if (allSeriesList.loading()) return;
    const count = allSeriesList.pages()?.length ?? 0;
    console.log("SeriesList resolved:", count);
    setLoadingStates("series", { count: count > 0 ? `+${count.toLocaleString()}` : "0", loading: false });
  });

  return (
    <View color={hexColor(C.bg)} onEscape={() => navigate("/")}>

      {/* Header */}
      <Header screen={t("home")}
        ref={(el) => headerRef = el}
        onRight={() => isRTL() ? null : col2?.setFocus()}
        onDown={() => heroRef?.setFocus()}
        onLeft={() => isRTL() ? settingRef?.setFocus() : null} />

      <View x={isRTL() ? 50 : 1700} y={50}>
        {/* VPN button */}
        <IconButton
          onRef={(el) => { col2 = el; }}
          x={0} y={0}
          src={isVpnMode()
            ? "https://i.ibb.co/Xfp2tGZS/diamond-Green.png"
            : "https://i.ibb.co/0p2DNKCg/diamond.png"}
          onRight={() => settingRef?.setFocus()}
          onLeft={() => isRTL() ? null : headerRef?.setFocus()}
          onDown={() => isRTL() ? moviesRef?.setFocus() : heroRef?.setFocus()}
          onEnter={togglePrivacyMode}
        />

        {/* Settings button */}
        <IconButton
          onRef={(el) => { settingRef = el; }}
          x={100} y={0}
          src="https://i.ibb.co/Jjp8bmn4/setting.png"
          onLeft={() => col2?.setFocus()}
          onDown={() => moviesRef?.setFocus()}
          onRight={() => isRTL() ? headerRef?.setFocus() : null}
          onEnter={() => navigate("/userInfo")}
        />
      </View>

      <HeroCard
        onRef={(el) => { heroRef = el; }}
        onEnter={liveTV}
        onRight={() => moviesRef?.setFocus()}
        onUp={() => isRTL() ? headerRef?.setFocus() : col2?.setFocus()}
      />

      <SideCard
        onRef={(el) => { moviesRef = el; }}
        x={rtlX(SIDE_LTR_X, SIDE_W, isRTL())} y={SIDE_TOP_Y}
        width={SIDE_W} height={SIDE_H}
        symbol="vod"
        title={t("bigMovies")}
        iconSrc="https://i.ibb.co/gMVRG9yG/video-play.png"
        imageSrc="https://i.ibb.co/1Gt5FHBf/movies-Bck.png"
        onEnter={movies}
        onLeft={() => isRTL() ? null : heroRef?.setFocus()}
        onRight={() => isRTL() ? heroRef?.setFocus() : null}
        onDown={() => seriesRef?.setFocus()}
        onUp={() => settingRef?.setFocus()}
      />

      {/* Side: Series */}
      <SideCard
        onRef={(el) => { seriesRef = el; }}
        x={rtlX(SIDE_LTR_X, SIDE_W, isRTL())} y={SIDE_BOT_Y}
        width={SIDE_W} height={SIDE_H}
        symbol="series"
        title={t("bigSeries")}
        iconSrc="https://i.ibb.co/yFrKTvtm/Video-Movies.png"
        imageSrc="https://i.ibb.co/gbz8Q9Rx/series-Bck.png"
        onEnter={series}
        onLeft={() => isRTL() ? null : heroRef?.setFocus()}
        onRight={() => isRTL() ? heroRef?.setFocus() : null}
        onUp={() => moviesRef?.setFocus()}
      />

      {/* Kill Switch Modal */}
      <Show when={showKillSwitchBlockModal()}>
        <View width={1920} height={1080} color={hexColor("#000000")} alpha={0.82} positionAbsolute={0} style={{ zIndex: 1999 }} />
        <View width={1920} height={1080} positionAbsolute={0} style={{ zIndex: 2000 }}>
          <View x={460} y={240} width={1000} height={500} color={hexColor("#0d1117")} borderRadius={24}
            style={{ borderWidth: 2, borderColor: hexColor(C.blue) }}>
            <View x={50} y={40}>
              <Text x={0} y={10} style={{ fontSize: 32, color: hexColor(C.textPrimary), fontWeight: isRTL() ? "normal" : "bold" }}>
                {t('access_restricted')}
              </Text>
            </View>
            <View x={50} y={120}>
              <Text x={0} y={0} style={{ fontSize: 28, color: hexColor(C.red), fontWeight: isRTL() ? "normal" : "bold" }}>
                {t('kSwitchProtecionActive')}
              </Text>
              <View x={0} y={60}>
                <Text x={0} y={0} style={{ fontSize: 24, color: hexColor(C.textPrimary) }}>
                  {t('enablePMode')}
                </Text>
                <Text x={0} y={50} style={{ fontSize: 20, color: hexColor(C.textMuted) }}>
                  {t('killSwitchProtecting')}
                </Text>
              </View>
              <View x={0} y={150}>
                <Text x={0} y={0} style={{ fontSize: 22, color: hexColor(C.blue), fontWeight: isRTL() ? "normal" : "bold" }}>
                  {t('to_access_playlists')}
                </Text>
                <Text x={0} y={40} style={{ fontSize: 20, color: hexColor(C.textPrimary) }}>
                  {t('enable_privacy_mode_step')}
                </Text>
                <Text x={0} y={70} style={{ fontSize: 20, color: hexColor(C.textPrimary) }}>
                  {t('disable_kill_switch_step')}
                </Text>
              </View>
            </View>
            <View
              ref={(el) => { killSwitchBlockModalOkRef = el; }}
              x={425} y={390}
              style={{
                borderRadius: 20,
                color: hexColor(C.blue),
                focus: { color: hexColor(C.blueDim), scale: 1.05, borderWidth: 2, borderColor: hexColor(C.blue) },
                transition: { scale: { duration: 200, easing: "ease-in-out" } },
                height: 60, width: 150,
              }}
              onEnter={() => { setShowKillSwitchBlockModal(false); col2?.setFocus(); }}
            >
              <Text x={50} y={16} style={{ fontSize: 24, color: hexColor("#ffffff"), fontWeight: isRTL() ? "normal" : "bold" }}>
                {t("ok")}
              </Text>
            </View>
          </View>
        </View>
      </Show>

    </View>
  );
};

export default HomePage;